export const siteMeta = {
  name: "Epsa Kaithwar",
  title: "Strategy, Research & Analytics",
  positioning:
    "I connect business understanding, structured problem-solving, and emerging AI capabilities to design practical approaches to business challenges.",
  headline: "I help turn complex business questions into clear strategic decisions.",
};

export const navLinks = [
  { label: "Work", to: "/work" },
  { label: "About", to: "/about" },
  { label: "Capabilities", to: "/capabilities" },
  { label: "Contact", to: "/contact" },
];

export const contact = {
  email: "kumari.epsa26@gmail.com",
  linkedin: "https://www.linkedin.com/in/epsa-k-b6a504a6/",
  linkedinLabel: "linkedin.com/in/epsa-k-b6a504a6",
};

export const homepage = {
  heroEyebrow: "Epsa Kaithwar — Strategy, Research & Analytics",
  heroIntro:
    "Over seven years across strategy, market and competitor research, competitive intelligence, analytics, and reporting transformation — now applied alongside pragmatic, hands-on experimentation with AI-enabled workflows.",
  aboutSummary:
    "My work sits at the intersection of business understanding, analytical thinking, and practical execution. I have spent seven-plus years in strategy, research, and analytics roles at KPMG, GlobalData, and Evalueserve, and I am now exploring how human expertise and AI capabilities can work together on real business problems.",
};

export const dimensions = [
  {
    title: "Established experience",
    body: "Strategy, market and competitor research, competitive intelligence, analytics, reporting transformation, and process improvement across seven-plus years at KPMG, GlobalData, and Evalueserve.",
  },
  {
    title: "Practical AI experimentation",
    body: "Building and testing AI-assisted workflows and prototypes that address real business and operational problems — evaluated pragmatically, not presented as finished commercial products.",
  },
  {
    title: "Emerging direction",
    body: "Exploring human–AI collaboration, workflow design, and practical business solutions through ongoing conversations with founders, business leaders, and potential collaborators.",
  },
];

export const capabilities = [
  {
    title: "Strategy",
    body: "Developing go-to-market strategies, evaluating commercial opportunities, and translating complex business questions into structured recommendations that support strategic decisions.",
  },
  {
    title: "Research",
    body: "Market and competitor research, competitive intelligence, benchmarking, market sizing, and sector analysis, with experience across insurance, financial services, private equity, and ESG.",
  },
  {
    title: "Analytics",
    body: "Turning commercial and operational data into actionable insights through KPI frameworks, pipeline analysis, performance reporting, and leadership reporting.",
  },
  {
    title: "Transformation",
    body: "Improving reporting processes and operating workflows by identifying inefficiencies, redesigning frameworks, strengthening governance, and establishing more consistent ways of working.",
  },
  {
    title: "AI-enabled workflows",
    body: "Designing and experimenting with AI-assisted workflows that combine structured decision rules, automation, and human review to improve research, operations, and business-development processes.",
  },
];

export const serviceAreas = [
  "Market & competitor research",
  "Benchmarking & opportunity assessment",
  "Market sizing",
  "GTM strategy & positioning",
  "Sector intelligence — insurance, financial services, private equity, ESG",
  "KPI & reporting design",
];

export const serviceNote =
  "Engagements are based on secondary research, analysis, and structured problem-solving. Primary research and customer interviews are not part of the current offer.";

export const projects = [
  {
    slug: "kpmg-gtm-reporting-transformation",
    title: "GTM Reporting Transformation",
    org: "KPMG",
    type: "Corporate case study",
    status: "Completed",
    featured: true,
    summary:
      "Redesigned reporting and mapping frameworks during a transition to a new go-to-market model — improving consistency and visibility across pipeline, bookings, and revenue, with more than 220 hours of documented annual efficiency savings.",
    tags: ["Reporting transformation", "Process improvement", "GTM"],
    caseStudy: {
      sections: [
        {
          title: "Context",
          text: "KPMG transitioned to a new go-to-market model, and the existing reporting and mapping frameworks no longer reflected how the business was organised. Reporting spanned weekly, monthly, and quarterly cadences, and needed to stay consistent and visible across pipeline, bookings, and revenue throughout the transition.",
        },
        {
          title: "Objective",
          text: "Redesign the reporting and mapping frameworks so reporting remained consistent and visible across pipeline, bookings, and revenue during the GTM transition.",
        },
        {
          title: "My approach",
          items: [
            "Redesigned the reporting and mapping frameworks to align with the new go-to-market model.",
            "Began with account-level reporting for selected Bridge Campaign accounts, proving the framework where the need was sharpest.",
            "Extended the approach from account-level application to sector-level implementation.",
            "Supported weekly, monthly, and quarterly reporting cadences throughout.",
          ],
        },
        {
          title: "Collaboration",
          text: "The work required coordination across reporting, transformation, finance, and global leadership teams. The framework redesign and mapping logic were my contribution; adoption and ongoing use were shared with the wider teams.",
        },
        {
          title: "Outcome",
          items: [
            "Improved reporting consistency and visibility across pipeline, bookings, and revenue.",
            "Documented annual efficiency savings of more than 220 hours.",
            "The approach progressed from account-level application to sector-level implementation.",
          ],
        },
        {
          title: "Key takeaway",
          text: "Proving the reporting approach at account level first created a framework strong enough to scale to sector level — with efficiency gains that could be documented, not assumed.",
        },
      ],
      capabilities: [
        "Structured problem-solving",
        "Reporting transformation",
        "Process improvement",
        "Connecting account-level activity with sector-level reporting",
      ],
    },
  },
  {
    slug: "esg-intelligence-commercial-opportunities",
    title: "Connecting ESG Intelligence to Commercial Opportunities",
    org: "Private Equity",
    type: "Corporate case study",
    status: "Completed",
    featured: true,
    summary:
      "A focused, one-week white-paper analysis in a Private Equity context — connecting ESG, regulatory, and investment-priority research to relevant advisory capabilities, potential cross-selling opportunities, and strategic account discussions.",
    tags: ["ESG intelligence", "Research synthesis", "Capability mapping"],
    caseStudy: {
      sections: [
        {
          title: "Context",
          text: "A white-paper analysis request in a Private Equity context, with an ESG focus — completed over approximately one week. The client is anonymised throughout this case study.",
        },
        {
          title: "Objective",
          text: "Translate ESG research into commercial relevance: connect the findings with relevant advisory capabilities to surface potential opportunities for strategic account discussions.",
        },
        {
          title: "My approach",
          items: [
            "Analysed the white paper and relevant material on ESG, regulatory trends, and investment priorities.",
            "Mapped relevant capabilities against the research findings.",
            "Identified potential advisory and cross-selling opportunities from that mapping.",
          ],
        },
        {
          title: "Collaboration",
          text: "I worked with campaign leaders to support the assessment and connect the research with relevant account and campaign priorities.",
        },
        {
          title: "Outcome",
          items: [
            "A capability map connecting ESG and regulatory developments to relevant advisory capabilities.",
            "Potential advisory and cross-selling opportunities identified for strategic account discussions.",
            "The analysis was used to support strategic account discussions and decision-making.",
          ],
        },
        {
          title: "Key takeaway",
          text: "Research becomes commercially relevant when findings are mapped directly to capabilities and account priorities — rather than presented for its own sake.",
        },
      ],
      capabilities: [
        "Research synthesis",
        "ESG intelligence",
        "Capability mapping",
        "Connecting external developments with business priorities",
      ],
    },
  },
  {
    slug: "rentthelook",
    title: "RentTheLook — Creator-First Clothing Rental Marketplace",
    org: "Independent project",
    type: "Independent product / MVP project",
    status: "MVP recognised",
    featured: true,
    summary:
      "An independent product/MVP project for a creator-first clothing rental marketplace — ranked among the Top 25 projects out of 4,000+ entries in Emergent Builder Fest, with prize money.",
    tags: ["Product thinking", "Marketplace", "MVP"],
    caseStudy: {
      sections: [
        {
          title: "Problem or opportunity",
          text: "Clothing rental is still built mostly around retailers. RentTheLook starts from a different premise: a creator-first clothing rental marketplace, where creators rent out looks and renters access them through the people who style them.",
        },
        {
          title: "Context and objective",
          text: "An independent product/MVP project, designed and built by me. The objective was to turn the concept into a working MVP and test it in a competitive product setting.",
        },
        {
          title: "My role and contribution",
          text: "Independent project end to end: the concept, product decisions, and MVP development were mine.",
        },
        {
          title: "Approach and key decisions",
          text: [
            "Designed RentTheLook as a creator-gated marketplace rather than an open peer-to-peer rental platform. The product architecture centres on Creator → Wardrobe → Item, with creator approval required before listings can be published.",
            "Developed the MVP through six structured phases using AI-assisted development, prioritising creator onboarding, wardrobe management, discovery, rental workflows, and functional validation. Deliberately deferred payments, logistics, and advanced features to keep the initial build focused.",
          ],
        },
        {
          title: "What was built",
          text: "A full-stack MVP built with React, FastAPI, and MongoDB: creator storefronts, wardrobe management, search and occasion-based discovery, rental requests, approval and cancellation workflows, availability controls, role-based permissions, automated tests, and a demonstration workflow. It is not yet a fully operational rental marketplace.",
        },
        {
          title: "Results and current status",
          items: [
            "RentTheLook ranked among the Top 25 projects out of 4,000+ entries in Emergent Builder Fest and received prize money.",
            "Current state: the payment system is not yet completed — this is an MVP, not an operating marketplace.",
          ],
        },
        {
          title: "Learnings and next steps",
          text: [
            "The development process demonstrated the importance of translating a marketplace concept into clear product rules, prioritising essential functionality, and validating core workflows before expanding scope.",
            "Next steps include completing the rental lifecycle, strengthening review security, introducing a usable review experience, and evaluating payment and logistics integration before a commercial launch.",
          ],
        },
      ],
      capabilities: ["Product thinking", "Marketplace design", "Independent MVP delivery"],
      note: "An independent MVP project — not an established operating business.",
    },
  },
  {
    slug: "emerging-tech-market-prioritisation",
    title: "Market Prioritisation for an Emerging Technology Business",
    org: "Anonymised",
    type: "Independent GTM strategy",
    status: "Completed",
    featured: true,
    summary:
      "A structured market-prioritisation assessment for an anonymised emerging technology business — comparing potential segments against commercial criteria to identify three areas worth further investigation.",
    tags: ["GTM strategy", "Market assessment", "Opportunity prioritisation"],
    caseStudy: {
      sections: [
        {
          title: "Context",
          text: "A complimentary first-cut assessment for an emerging technology business — intended to stimulate discussion and support further exploration, not a paid engagement or a validated market-entry decision. The business is anonymised throughout.",
        },
        {
          title: "Objective",
          text: "Give the business a structured way to evaluate potential markets and identify promising initial areas of focus.",
        },
        {
          title: "My approach",
          text: "Developed a market-prioritisation assessment comparing potential segments against commercial criteria, including buyer fit, frequency of communication needs, repeat-deployment potential, and suitability for self-serve adoption.",
        },
        {
          title: "Outcome",
          items: [
            "A structured assessment identifying three potential segments for further investigation: public infrastructure construction, water utilities, and EV charging.",
          ],
        },
      ],
      capabilities: ["Market assessment", "Opportunity prioritisation", "Commercial criteria design"],
    },
  },
  {
    slug: "crm-pipeline-hygiene",
    title: "CRM Pipeline Hygiene & Risk Review Workflow",
    org: "Independent project",
    type: "Independent workflow design",
    status: "Design stage",
    featured: false,
    summary:
      "A rules-first workflow design for more reliable CRM pipeline reviews, distinguishing data-quality errors from genuinely stalled opportunities and their potential commercial risks. The proposed approach prioritises structured validation, human-approved corrections, and selective AI assistance for interpreting unstructured information.",
    tags: ["CRM data quality", "Pipeline governance", "Workflow design"],
    note: "Decision logic under review; not yet built, tested, or commercially validated. Independent project, not affiliated with any former employer.",
  },
  {
    slug: "rate-ledger",
    title: "Rate Ledger — Freelancer Pricing Calculator",
    org: "Independent project",
    type: "Independent Project · Prototype",
    status: "Prototype",
    featured: false,
    summary:
      "A browser-based pricing calculator designed to help freelancers set sustainable rates based on income goals, business expenses, billable hours, and tax buffers. Built with Claude's assistance, it calculates hourly and project rates, explains the underlying pricing logic, and provides tools for estimating scope changes and establishing a private minimum acceptable rate.",
    tags: ["Product thinking", "Pricing logic", "AI-assisted development"],
    note: "Independent prototype — not commercially launched or validated.",
  },
  {
    slug: "gtm-engineering-workflow",
    title: "GTM Engineering Workflow",
    org: "Independent project",
    type: "Work in development",
    status: "In development",
    featured: false,
    summary:
      "An evolving, human-reviewed approach to market assessment and opportunity prioritisation — exploring how businesses can move from broad market questions towards structured, evidence-informed decisions.",
    tags: ["GTM", "Market assessment", "AI-enabled workflow"],
  },
  {
    slug: "ai-assisted-networking",
    title: "AI-Assisted Networking & Relationship Management",
    org: "Independent workflow project",
    type: "AI-assisted independent workflow",
    status: "In active use",
    featured: false,
    summary:
      "Designed two connected, AI-assisted tools to streamline networking and relationship management. The Networking Room Tracker uses scheduled AI research to identify relevant events, organise preparation and follow-up tasks, and flag calendar conflicts. The Conversation Guide tracks contacts, conversation context, and next steps, with daily synchronisation between the two tools.",
    tags: ["AI-assisted workflow", "Operations", "Relationship management"],
    note: "Private workflows built for my own networking, relationship management, and business development — not publicly accessible.",
  },
  {
    slug: "aurora-indian-ev-gtm",
    title: "Aurora — Indian EV Market Entry GTM",
    org: "Portfolio project",
    type: "Hypothetical portfolio project",
    status: "Hypothetical",
    featured: false,
    summary:
      "A hypothetical portfolio project exploring market-entry strategy for the Indian EV market — structured as a demonstration of GTM thinking, not client work.",
    tags: ["Market entry", "GTM strategy", "EV"],
    note: "A hypothetical portfolio project — not client work.",
  },
];

export const experience = [
  {
    org: "KPMG",
    role: "Consultant — Global Clients & Markets (Capability Hubs)",
    dates: "May 2024 — Dec 2025",
    detail:
      "Strategy, analytics, and reporting transformation — including GTM reporting redesign during a go-to-market model transition.",
  },
  {
    org: "Evalueserve",
    role: "Senior Analyst",
    dates: "Dec 2022 — Dec 2023",
    detail:
      "Market intelligence, sector research, and competitive benchmarking across insurance, energy, healthcare, and education.",
  },
  {
    org: "GlobalData",
    role: "Analyst / Associate Analyst — Financial Services (Insurance)",
    dates: "Nov 2019 — Dec 2022",
    detail: "Market research, competitive intelligence, and sector analysis.",
  },
];
